import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import type {
  CompanyMember,
  Customer,
  Job,
  JobAuditLog,
  JobState,
  MemberRole,
  SubcontractorRecord,
} from '../types/models';
import {
  canBookJobs,
  canDispatchJobs,
  canExcludeEvidence,
  canInvoiceJobs,
  canPriceJobs,
  canUnlockInvoices,
  canViewJobFinancials,
  canViewJobHistory,
  imageHasValidLocation,
  isFinancialAuditEntry,
  JOB_ARCHIVE_STATES,
  jobStateLabel,
  legalTransitionsFrom,
} from '../types/models';
import {
  changeJobState,
  deleteJobPermanently,
  updateJob as updateJobCallable,
} from '../lib/jobsBackend';
import { StatusBadge } from '../components/StatusBadge';
import { AssigneeTag, CourtesyCarTag, NeedsDetailsMark } from '../components/JobTags';
import { AccountingBadge } from '../components/AccountingBadge';
import {
  formatCoords,
  formatMoneyPence,
  formatTimestamp,
  titleCaseStatus,
} from '../lib/format';
import { PhotoGallery } from '../components/PhotoGallery';
import { PhotoUploader } from '../components/PhotoUploader';
import { AttachmentUploader } from '../components/AttachmentUploader';
import { ContactDetails, ContactFields } from '../components/ContactFields';
import { useCompanyFeatures } from '../hooks/useCompanyFeatures';
import { contactErrors, contactForm, contactPayload, type ContactForm } from '../lib/contacts';
import { CustomerPicker } from '../components/CustomerPicker';
import { FUEL_TYPES, TRANSMISSIONS, pickerOptions } from '../lib/jobPickers';
import {
  ChevronDown, ChevronLeft, Copy, MapPin, Pencil, Trash2, UserPlus,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Field, OutstandingMark, Select as FieldSelect, TextArea, TextInput, useFieldId } from '../components/ui/Field';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { Switch } from '../components/ui/Switch';
import { Alert } from '../components/ui/Alert';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/Confirm';
import { LinkedText } from '../components/LinkedText';
import { UnlockInvoiceModal } from '../components/UnlockInvoiceModal';
import { usePageTitle } from '../hooks/usePageTitle';
import { ReadFailure } from '../components/ReadFailure';
import { listenDocument, listenQuery } from '../lib/liveRead';
import { JobBookingPanel } from '../components/JobBookingPanel';
import { withReadTimeout } from '../lib/readLoading';
import { missingCompletionFields } from '../lib/completion';
import { useJobFinance, useJobFinanceStatus, withJobFinance } from '../lib/jobFinance';
import { JobPriceModal } from '../components/JobPriceModal';
import { useChargeFlags } from '../hooks/useChargeFlags';
import { canViewChargeFlags } from '../types/models';
import { assignmentChangeDetails, mergeJobHistory, readableHistoryField, readableHistoryValue } from '../lib/jobHistory';

interface Props {
  archived?: boolean;
}

type EditableJobField =
  | 'registrationNumber'
  | 'make'
  | 'model'
  | 'colour'
  | 'fuelType'
  | 'transmission'
  | 'vehicleMileage'
  | 'customerName'
  | 'pickupLocation'
  | 'dropoffLocation'
  | 'courtesyCarRegistration'
  | 'courtesyCarMileage';

type JobFormState = Record<EditableJobField, string> & ContactForm & {
  courtesyCarRequired: boolean;
  notes: string;
};

const EDITABLE_FIELDS: Array<{
  key: EditableJobField;
  label: string;
}> = [
  { key: 'registrationNumber', label: 'Registration' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'colour', label: 'Colour' },
  { key: 'fuelType', label: 'Fuel' },
  { key: 'transmission', label: 'Transmission' },
  // The casualty vehicle's own reading, courtesy car or not; optional (30 Oct 2026).
  { key: 'vehicleMileage', label: 'Mileage (optional)' },
  { key: 'customerName', label: 'Customer' },
  { key: 'pickupLocation', label: 'Pickup' },
  { key: 'dropoffLocation', label: 'Dropoff' },
];

export default function JobDetailPage({ archived = false }: Props) {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { activeCompanyId, activeRole, user } = useAuth();
  const toast = useToast();
  const companyFeatures = useCompanyFeatures();
  const chargeFlags = useChargeFlags(jobId, companyFeatures.additionalChargesEnabled);
  const confirm = useConfirm();
  const editFormId = useFieldId('job-edit');
  const [rawJob, setJob] = useState<Job | null>(null);
  // The price and invoice details come from the protected price record (lib/jobFinance),
  // merged in so the price section below reads job.pricing as before.
  const jobFinance = useJobFinance(jobId);
  const financeStatus = useJobFinanceStatus();
  const job = useMemo(() => (rawJob ? withJobFinance(rawJob, jobFinance) : null), [rawJob, jobFinance]);
  const [history, setHistory] = useState<{
    scope: string; logs: JobAuditLog[]; finance: JobAuditLog[];
    loading: boolean; financeLoading: boolean; error: string | null; financeError: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editScope, setEditScope] = useState<string | null>(null);
  const [form, setForm] = useState<JobFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showAssignment, setShowAssignment] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [unlockInvoiceScope, setUnlockInvoiceScope] = useState<string | null>(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const lifecycleExitRef = useRef(false);

  const canSeeFinancials = canViewJobFinancials(activeRole ?? undefined);
  const canSeeHistory = canViewJobHistory(activeRole ?? undefined);
  // Price and invoice entries carry amounts; only roles that may see prices get them.
  const historyScope = JSON.stringify([user?.uid, activeCompanyId, jobId, activeRole]);
  const jobScope = JSON.stringify([user?.uid, activeCompanyId, jobId]);
  const editingCurrentJob = isEditing && editScope === jobScope;
  useEffect(() => { setIsEditing(false); setForm(null); setEditScope(null); }, [jobScope]);
  // A dismissed authorization scope must never reopen its old confirmation if
  // that role/company comes back later, or the job is invoiced again.
  useEffect(() => { setUnlockInvoiceScope(null); setShowPricing(false); }, [historyScope, rawJob?.accountingStatus]);
  const currentHistory = history?.scope === historyScope ? history : null;
  const logsError = currentHistory?.error;
  const visibleLogs = mergeJobHistory(
    (currentHistory?.logs ?? []).filter(l => canSeeFinancials || !isFinancialAuditEntry(l)),
    canSeeFinancials ? currentHistory?.finance ?? [] : []
  );
  const canCorrectEvidence = canExcludeEvidence(activeRole ?? undefined);

  usePageTitle(job?.registrationNumber || 'Job');

  useEffect(() => {
    if (!activeCompanyId || !jobId) {
      setLoading(false);
      return;
    }
    // PR-26: one collection for the whole lifecycle. The /jobs and
    // /jobs/archived routes both read here; the route is kept in sync with
    // the document's state below rather than by probing two collections.
    const jobRef = doc(db, 'companies', activeCompanyId, 'jobs', jobId);
    setLoading(true);
    setJob(null);
    setError(null);
    const unsub = listenDocument(
      jobRef,
      (snap) => {
        if (!snap.exists()) {
          setJob(null);
          setError('Job not found.');
        } else {
          const data = snap.data() as Omit<Job, 'id'>;
          const nextJob = { id: snap.id, ...data } as Job;
          const isArchivedJob = JOB_ARCHIVE_STATES.includes(nextJob.state);
          if (isArchivedJob !== archived) {
            // Completion and a deliberate archive both return to Live Jobs.
            // External lifecycle changes still route to the matching detail.
            if (lifecycleExitRef.current || !isArchivedJob) {
              lifecycleExitRef.current = false;
              navigate('/jobs', { replace: true });
            } else {
              navigate(`/jobs/archived/${jobId}`, { replace: true });
            }
            return;
          }
          setJob(nextJob);
          setError(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [activeCompanyId, jobId, archived, navigate, readAttempt]);

  useEffect(() => {
    if (job && !isEditing) setForm(formFromJob(job));
  }, [job, isEditing]);

  useEffect(() => {
    setIsEditing(false);
    setForm(null);
    setCustomers([]);
    setCustomerError(null);
    setShowAssignment(false);
    setShowPricing(false);
    lifecycleExitRef.current = false;
  }, [activeCompanyId, jobId]);

  useEffect(() => {
    if (!activeCompanyId || !jobId || !user || !canSeeHistory) {
      setHistory(null);
      return;
    }
    let active = true;
    const scope = historyScope;
    setHistory({ scope, logs: [], finance: [], loading: true, financeLoading: canSeeFinancials, error: null, financeError: null });
    const update = (change: Partial<NonNullable<typeof history>>) => {
      if (active) setHistory(previous => previous?.scope === scope ? { ...previous, ...change } : previous);
    };
    const entries = (snap: { docs: { id: string; data(): unknown }[] }) => snap.docs.map(d => ({
      ...d.data() as Omit<JobAuditLog, 'id'>, id: d.id,
    } as JobAuditLog));
    const logsRef = collection(
      db,
      'companies',
      activeCompanyId,
      'jobs',
      jobId,
      'auditLogs'
    );
    const q = query(logsRef, orderBy('timestamp', 'asc'));
    const unsub = listenQuery(
      q,
      (snap) => {
        update({ logs: entries(snap), loading: false, error: null });
      },
      (err) => {
        console.error('audit log listener', err);
        update({ logs: [], loading: false, error: err.message });
      }
    );
    const unsubscribeFinance = canSeeFinancials ? listenQuery(
      query(collection(db, 'companies', activeCompanyId, 'jobFinance', jobId, 'history'), orderBy('timestamp', 'asc')),
      snap => update({ finance: entries(snap), financeLoading: false, financeError: null }),
      err => update({ finance: [], financeLoading: false, financeError: err.message })
    ) : undefined;
    return () => { active = false; unsub(); unsubscribeFinance?.(); };
  }, [activeCompanyId, jobId, user?.uid, canSeeHistory, canSeeFinancials, historyScope, readAttempt]);

  useEffect(() => {
    if (!activeCompanyId) {
      setCustomers([]);
      return;
    }

    let cancelled = false;
    setCustomerError(null);
    void withReadTimeout(getDocs(
      query(
        collection(db, 'companies', activeCompanyId, 'customers'),
        orderBy('name')
      )
    ))
      .then((snap) => {
        if (cancelled) return;
        setCustomers(
          snap.docs
            .map((customerDoc) => ({
              id: customerDoc.id,
              ...(customerDoc.data() as Omit<Customer, 'id'>),
            }))
            .filter((customer) => customer.isActive !== false)
        );
      })
      .catch((loadError) => {
        console.warn('Failed to load customers', loadError);
        if (!cancelled) {
          setCustomerError(loadError instanceof Error ? loadError.message : 'Customer picker could not be loaded.');
          setCustomers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeCompanyId, readAttempt]);

  if (loading) return <div className="text-sm text-ink2">Loading...</div>;
  if (error)
    return <ReadFailure message={error} retry={() => setReadAttempt((n) => n + 1)} />;
  if (!job) return null;

  const canEdit = canEditJob({
    archived,
    job,
    role: activeRole,
    uid: user?.uid,
  });
  const canAssign =
    !archived &&
    job.state !== 'completed' &&
    canDispatchJobs(activeRole ?? undefined);
  // PR-26: one delete callable for every state (superadmin-only, audited).
  const canDeleteJob =
    activeRole === 'superadmin' &&
    (job.state === 'pending' || job.state === 'cancelled');
  const canPriceJob =
    !!activeCompanyId &&
    canSeeFinancials &&
    canPriceJobs(activeRole ?? undefined) && financeStatus.ready &&
    (job.state === 'completed' || job.state === 'archived') &&
    job.accountingStatus !== 'invoiced';
  const canUnlockJob = !!activeCompanyId && canUnlockInvoices(activeRole ?? undefined)
    && financeStatus.ready && job.accountingStatus === 'invoiced';
  // Archive/restore are dispatch-level transitions (invariant 5).
  const canArchive =
    !archived &&
    canDispatchJobs(activeRole ?? undefined) &&
    (job.state === 'completed' || job.state === 'cancelled');
  const canRestore =
    archived &&
    canDispatchJobs(activeRole ?? undefined) &&
    JOB_ARCHIVE_STATES.includes(job.state);
  const statusOptions = legalTransitionsFrom(job.state).filter(
    (next) => next !== 'archived'
  );

  // One control for state. It lists only what the server's state machine would
  // accept from here, and shows why completion is unavailable rather than hiding it.
  const missingNow = missingCompletionFields(job);
  const statusChoices: Array<{ value: string; label: string; disabled?: boolean }> = [
    { value: job.state, label: `${jobStateLabel(job.state)} — current` },
  ];
  if (canEdit && !archived) {
    for (const next of statusOptions) {
      if (next === 'completed') {
        statusChoices.push({
          value: 'completed',
          label: missingNow.length > 0
            ? `Completed — ${missingNow.length} detail${missingNow.length === 1 ? '' : 's'} missing`
            : 'Completed',
          disabled: missingNow.length > 0,
        });
      } else {
        statusChoices.push({ value: next, label: jobStateLabel(next) });
      }
    }
  }
  if (canArchive) statusChoices.push({ value: 'archived', label: 'Archived' });
  if (canRestore) statusChoices.push({ value: 'restore', label: 'Restore to Live jobs' });

  const currentState = job.state;
  function chooseStatus(next: string) {
    if (next === currentState) return;
    if (next === 'restore') { void restoreJob(); return; }
    void updateStatus(next as JobState);
  }

  const changedFields = form && editScope === jobScope ? collectChangedFields(job, form, companyFeatures.contactEmailEnabled) : {};
  const hasChanges = Object.keys(changedFields).length > 0;

  async function cancelEdits() {
    if (saving) return;
    if (hasChanges && !(await confirm({
      title: 'Discard unsaved changes?',
      message: 'The job has not been changed. Discard only the edits you have not saved?',
      confirmLabel: 'Discard changes', cancelLabel: 'Keep editing',
    }))) return;
    setForm(formFromJob(job!));
    setIsEditing(false);
    setActionError(null);
  }

  // PR-26: audit entries are server-authored by the callables — client writes
  // to the jobs auditLogs subcollection are denied by rules.

  async function saveEdits(event: FormEvent) {
    event.preventDefault();
    const currentJob = job;
    if (!activeCompanyId || !jobId || !currentJob || !form || editScope !== jobScope || !canEdit || archived) {
      return;
    }

    const invalidContacts = Object.values(contactErrors(form, companyFeatures.contactEmailEnabled));
    if (invalidContacts.length) { setActionError(invalidContacts[0]!); return; }
    const changes = collectChangedFields(currentJob, form, companyFeatures.contactEmailEnabled);
    if (Object.keys(changes).length === 0) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    setActionError(null);
    setNotice(null);
    try {
      // Field-level allowlists, validation and the audit trail all live in
      // the updateJob callable (optimistic version locking is retired —
      // the server is the single writer).
      await updateJobCallable({
        companyId: activeCompanyId,
        jobId,
        updates: changes,
      });

      setIsEditing(false);
      setNotice('Job updated.');
    } catch (e) {
      console.error(e);
      setActionError(friendlyCallableError(e));
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(state: JobState) {
    const currentJob = job;
    if (!activeCompanyId || !jobId || !currentJob || state === currentJob.state) {
      return;
    }

    if (state === 'completed') {
      const missing = missingCompletionFields(currentJob).map((item) => item.label);
      if (missing.length > 0) {
        setActionError(
          `Complete these fields before marking the job completed: ${missing.join(', ')}.`
        );
        return;
      }
    }

    if (state === 'archived') {
      const ok = await confirm({
        title: 'Archive job?',
        message:
          'This moves the job to the archive. Evidence and history stay attached, and a dispatcher can restore it.',
        confirmLabel: 'Archive job',
      });
      if (!ok) return;
    }

    setSaving(true);
    setActionError(null);
    setNotice(null);
    lifecycleExitRef.current =
      !archived && (state === 'completed' || state === 'archived');
    try {
      // The server's state machine validates the transition and writes the
      // audit entry. Archive/restore are one-field updates now — no copying.
      const result = await changeJobState({ companyId: activeCompanyId, jobId, state });
      if (state === 'archived') {
        toast.success('Job archived.');
        navigate('/jobs');
      } else if (state === 'completed') {
        toast.success(
          result.toState === 'archived'
            ? 'Job completed and moved to Archive.'
            : 'Job completed.'
        );
        navigate('/jobs');
      } else {
        setNotice('Status updated.');
      }
    } catch (e) {
      lifecycleExitRef.current = false;
      console.error(e);
      setActionError(friendlyCallableError(e));
    } finally {
      setSaving(false);
    }
  }

  async function restoreJob() {
    if (!activeCompanyId || !jobId || !job || !canRestore) return;
    setSaving(true);
    setActionError(null);
    setNotice(null);
    try {
      // Mirror iOS: archived -> completed is the legal restore hop, then
      // completed -> in_progress puts the job back on the Live Jobs board.
      if (job.state === 'archived') {
        await changeJobState({
          companyId: activeCompanyId,
          jobId,
          state: 'completed',
        });
      }
      await changeJobState({
        companyId: activeCompanyId,
        jobId,
        state: 'in_progress',
      });
      toast.success('Job restored to Live Jobs.');
      navigate('/jobs');
    } catch (restoreError) {
      console.error(restoreError);
      setActionError(friendlyCallableError(restoreError));
    } finally {
      setSaving(false);
    }
  }

  async function copyAddress(address: string | null | undefined, label: string) {
    const cleanAddress = address?.trim();
    if (!cleanAddress) return;
    try {
      await navigator.clipboard.writeText(cleanAddress);
      toast.success(`${label} address copied.`);
    } catch (copyError) {
      console.error(copyError);
      toast.error('The browser could not copy that address.');
    }
  }

  async function appendNote(event: FormEvent) {
    event.preventDefault();
    const cleanNote = newNote.trim();
    if (!activeCompanyId || !jobId || !job || !canEdit || !cleanNote) return;
    setSaving(true);
    setActionError(null);
    try {
      // No date in the text (Bobby, 1 Nov 2026): the job's history records when,
      // who, and the notes before and after.
      const entry = cleanNote;
      const existing = job.notes?.trim() ? job.notes : '';
      await updateJobCallable({
        companyId: activeCompanyId,
        jobId,
        updates: { notes: existing ? `${existing}\n\n${entry}` : entry },
      });
      setNewNote('');
      setShowAddNote(false);
      toast.success('Note added.');
    } catch (noteError) {
      console.error(noteError);
      setActionError(friendlyCallableError(noteError));
    } finally {
      setSaving(false);
    }
  }

  async function deleteJob() {
    if (!activeCompanyId || !jobId || !job || !canDeleteJob) return;
    const expected = (job.registrationNumber || jobId).trim().toUpperCase();
    const confirmed = await confirm({
      title: 'Permanently delete job?',
      message:
        'This permanently deletes the job and its evidence. This action cannot be undone.',
      confirmLabel: 'Permanently delete',
      tone: 'danger',
      requireText: expected,
    });
    if (!confirmed) return;

    setSaving(true);
    setActionError(null);
    setNotice(null);
    try {
      // One callable for every state — the legacy live/archived delete pair
      // merged with the collections (N-08 hazard class gone).
      const result = await deleteJobPermanently({
        companyId: activeCompanyId,
        jobId,
      });
      if (result.storageCleanupPending) {
        toast.warning(
          'The job was deleted, but Storage cleanup is pending retry. The company audit log records this.'
        );
      } else {
        toast.success('Job permanently deleted.');
      }
      navigate(archived ? '/archive' : '/jobs');
    } catch (e) {
      console.error(e);
      setActionError(friendlyCallableError(e));
    } finally {
      setSaving(false);
    }
  }

  const missing = missingCompletionFields(job);
  const vehicleLine = [job.make, job.model].filter(Boolean).join(' ');
  const isLive = job.state === 'pending' || job.state === 'in_progress';

  return (
    <div>
      {canUnlockJob && activeCompanyId && unlockInvoiceScope === historyScope && (
        <UnlockInvoiceModal key={historyScope} companyId={activeCompanyId} job={job}
          onClose={() => setUnlockInvoiceScope(null)}
          onUnlocked={() => { setUnlockInvoiceScope(null); toast.success('Invoice unlocked.'); }} />
      )}
      <Link
        to={archived ? '/archive' : '/jobs'}
        className="mb-2.5 inline-flex items-center gap-1 text-[15px] font-medium text-accent hover:underline"
      >
        <ChevronLeft size={16} strokeWidth={2} /> {archived ? 'Archive' : 'Live jobs'}
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        {/* The plate treatment is for entry and a job's own header only. */}
        <span className="reg-plate inline-flex h-11 items-center px-3.5 text-[24px]">
          {job.registrationNumber || 'No reg'}
        </span>
        <StatusBadge status={job.state} />
        {job.courtesyCarRequired && <CourtesyCarTag />}
        <AssigneeTag job={job} />
        <NeedsDetailsMark job={job} withText />
        <JobBookingPanel key={`${activeCompanyId}/${jobId}`} job={job} companyId={activeCompanyId} canBook={!archived && canEdit && canBookJobs(activeRole ?? undefined)} />
        {canSeeFinancials && financeStatus.ready && (job.state === 'completed' || job.state === 'archived' || job.accountingStatus) && (
          <AccountingBadge status={job.accountingStatus} />
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* State is server-owned: the picker offers only the transitions the state
              machine would accept, and completion carries its own blocking reason. */}
          {statusChoices.length > 1 && (
            <>
              <label htmlFor="job-status" className="sr-only">Job status</label>
              <FieldSelect
                id="job-status"
                value={job.state}
                disabled={saving}
                onChange={(event) => chooseStatus(event.target.value)}
                className="h-10 w-full max-w-[210px] text-[14.5px]"
              >
                {statusChoices.map((choice) => (
                  <option key={choice.value} value={choice.value} disabled={choice.disabled}>
                    {choice.label}
                  </option>
                ))}
              </FieldSelect>
            </>
          )}
          {canAssign && !archived && (
            <Button type="button" variant="secondary" size="sm" icon={UserPlus}
              onClick={() => { setShowAssignment(true); setActionError(null); setNotice(null); }}>
              {job.assignmentType ? 'Reassign' : 'Assign'}
            </Button>
          )}
          {editingCurrentJob ? (
            <>
              <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={() => void cancelEdits()}>Cancel edit</Button>
              <Button type="submit" form={editFormId} size="sm" loading={saving} disabled={!hasChanges || !canEdit || (!!form && Object.keys(contactErrors(form, companyFeatures.contactEmailEnabled)).length > 0)}>Save changes</Button>
            </>
          ) : canEdit && !archived && (
            <Button type="button" variant="secondary" size="sm" icon={Pencil}
              onClick={() => { setForm(formFromJob(job)); setEditScope(jobScope); setIsEditing(true); setActionError(null); setNotice(null); }}>
              Edit job
            </Button>
          )}
          {canDeleteJob && (
            <Button type="button" variant="danger-outline" size="sm" icon={Trash2} disabled={saving} onClick={deleteJob}>
              Delete job
            </Button>
          )}
        </div>
      </div>

      <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] text-ink2">
        <span className={vehicleLine ? '' : 'font-medium text-outstanding'}>
          {[vehicleLine, job.colour].filter(Boolean).join(' · ') || 'Vehicle details needed'}
        </span>
        <span className="text-ink3">· Created {formatTimestamp(job.clientCreatedAt ?? job.createdAt)}</span>
        {job.createdBy?.displayName && <span className="text-ink3">by {job.createdBy.displayName}</span>}
      </p>

      {canEdit && !archived && isLive && missing.length > 0 && (
        <p className="mt-2.5 text-[13.5px] text-ink3">
          Cannot be completed yet — still needed:{' '}
          <span className="text-outstanding">{missing.map((m) => m.label).join(', ')}</span>. Use Edit job to add them.
        </p>
      )}

      <div className="mt-4 space-y-2 empty:hidden">
        {actionError && <Alert tone="error">{actionError}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
        {!canEdit && (
          <Alert tone="info">
            You can view this job. Editing follows the iOS permissions: dispatch roles can edit live
            jobs, drivers can edit jobs assigned to them, and archived jobs are admin-only.
          </Alert>
        )}
      </div>

      {showAssignment && activeCompanyId && (
        <AssignmentModal companyId={activeCompanyId} job={job} onClose={() => setShowAssignment(false)} />
      )}
      {showPricing && activeCompanyId && financeStatus.ready && (
        <JobPriceModal companyId={activeCompanyId} job={job} onClose={() => setShowPricing(false)} />
      )}
      {showAddNote && (
        <Modal open onClose={() => setShowAddNote(false)} title="Add note">
          <form onSubmit={appendNote} className="space-y-4">
            {job.notes && (
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-[10px] bg-surface p-3 text-[14px] text-ink2">
                <LinkedText text={job.notes} />
              </div>
            )}
            <Field label="Further notes">
              <TextArea value={newNote} onChange={(event) => setNewNote(event.target.value)} rows={4} autoFocus
                placeholder="Add further notes" />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setShowAddNote(false)}>Cancel</Button>
              <Button type="submit" loading={saving} disabled={!newNote.trim()}>Add note</Button>
            </div>
          </form>
        </Modal>
      )}

      {editingCurrentJob && form && (
        <Panel className="mt-5">
          <PanelTitle title="Edit job" />
          <form id={editFormId} onSubmit={saveEdits}>
            <div className="grid gap-4 p-5 pt-4 sm:grid-cols-2">
              {EDITABLE_FIELDS.map((field) => {
                const owed = missing.some((item) => item.field === field.key);
                const label = <Field key={field.key} label={field.label} outstanding={owed}
                  className={field.key === 'customerName' ? 'sm:col-span-2' : undefined}
                  hint={field.key === 'customerName' && customerError ? (
                    <span className="text-st-cancelled">
                      {customerError}{' '}
                      <button type="button" onClick={() => setReadAttempt((n) => n + 1)} className="font-semibold text-accent hover:underline">Retry</button>
                    </span>
                  ) : undefined}>
                  {field.key === 'fuelType' || field.key === 'transmission' ? (
                    <FieldSelect value={form[field.key]} outstanding={owed}
                      onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}>
                      {pickerOptions(field.key === 'fuelType' ? FUEL_TYPES : TRANSMISSIONS, form[field.key]).map((option) => (
                        <option key={option || 'empty'} value={option}>
                          {option || (field.key === 'fuelType' ? 'Choose fuel' : 'Choose transmission')}
                        </option>
                      ))}
                    </FieldSelect>
                  ) : field.key === 'customerName' ? (
                    <CustomerPicker
                      customers={customers}
                      value={form[field.key]}
                      outstanding={owed}
                      onChange={(value, customer) => {
                        const nextForm = { ...form, [field.key]: value };
                        const address = customer?.companyAddress?.trim();
                        const currentDropoff = nextForm.dropoffLocation.trim();
                        if (address && (!currentDropoff || currentDropoff === 'Unknown')) {
                          nextForm.dropoffLocation = address;
                        }
                        setForm(nextForm);
                      }}
                    />
                  ) : (
                    <TextInput
                      value={form[field.key]}
                      outstanding={owed}
                      className={field.key === 'registrationNumber' ? 'font-mono uppercase tracking-[0.04em]' : undefined}
                      onChange={(event) => setForm({
                        ...form,
                        [field.key]: field.key === 'registrationNumber' ? event.target.value.toUpperCase() : event.target.value,
                      })}
                    />
                  )}
                </Field>;
                return field.key === 'pickupLocation' || field.key === 'dropoffLocation'
                  ? <div key={field.key}>{label}<ContactFields kind={field.key === 'pickupLocation' ? 'collection' : 'delivery'}
                      value={form} onChange={(key, value) => setForm({ ...form, [key]: value })}
                      emailEnabled={companyFeatures.contactEmailEnabled} disabled={saving} /></div>
                  : label;
              })}
            </div>

            <div className="border-t border-rowhair">
              <div className="flex min-h-14 items-center justify-between gap-4 px-5 py-3.5">
                <div>
                  <div className="text-[15.5px] font-semibold text-ink">Courtesy car supplied</div>
                  <div className="mt-0.5 text-[13.5px] text-ink3">Adds a Courtesy Car evidence heading to this job.</div>
                </div>
                <Switch label="Courtesy car supplied" checked={form.courtesyCarRequired}
                  onChange={(checked) => setForm({ ...form, courtesyCarRequired: checked })} />
              </div>
              {form.courtesyCarRequired && (
                <div className="grid gap-4 border-t border-rowhair p-5 sm:grid-cols-2">
                  <Field label="Courtesy registration" outstanding={missing.some((m) => m.field === 'courtesyCarRegistration')}>
                    <TextInput value={form.courtesyCarRegistration} className="font-mono uppercase tracking-[0.04em]"
                      outstanding={missing.some((m) => m.field === 'courtesyCarRegistration')}
                      onChange={(event) => setForm({ ...form, courtesyCarRegistration: event.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="Courtesy mileage (optional)">
                    <TextInput value={form.courtesyCarMileage} inputMode="numeric"
                      onChange={(event) => setForm({ ...form, courtesyCarMileage: event.target.value })} />
                  </Field>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-rowhair px-5 py-3.5">
              <Button type="button" variant="secondary"
                disabled={saving} onClick={() => void cancelEdits()}>
                Cancel
              </Button>
              <Button type="submit" disabled={!hasChanges || !canEdit || (!!form && Object.keys(contactErrors(form, companyFeatures.contactEmailEnabled)).length > 0)} loading={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </Panel>
      )}

      <div className="mt-5 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        {/* Left: what the job is. Right: what was filed against it. */}
        <div className="flex flex-col gap-5">
          {!editingCurrentJob && (<>
          <Panel>
            <PanelTitle title="Vehicle" owed={missing.filter((m) => ['make', 'model', 'colour', 'fuelType', 'transmission'].includes(m.field)).length} />
            <div className="px-5 pb-1 pt-2">
              <KV label="Make" value={job.make} />
              <KV label="Model" value={job.model} />
              <KV label="Colour" value={job.colour} />
              <KV label="Fuel" value={job.fuelType} />
              <KV label="Transmission" value={job.transmission} />
              <KV label="Mileage" value={job.vehicleMileage} mono />
            </div>
          </Panel>

          {(job.courtesyCarRequired || job.courtesyCarRegistration || job.courtesyCarMileage) && (
            <Panel>
              <PanelTitle title="Courtesy car" owed={missing.filter((m) => m.field.startsWith('courtesy')).length} />
              <div className="px-5 pb-1 pt-2">
                <KV label="Supplied" value={job.courtesyCarRequired ? 'Yes' : 'No'} />
                <KV label="Registration" value={job.courtesyCarRegistration} mono />
                <KV label="Courtesy mileage" value={job.courtesyCarMileage} mono />
              </div>
            </Panel>
          )}

          <Panel>
            <PanelTitle title="Details" owed={missing.filter((m) => ['customerName', 'pickupLocation', 'dropoffLocation'].includes(m.field)).length} />
            <div className="px-5 pb-1 pt-2">
              <KV label="Customer" value={job.customerName} />
              <KV label="Collect from" value={job.pickupLocation} onMap={() => openMaps(job.pickupLocation)}
                onCopy={() => copyAddress(job.pickupLocation, 'Pickup')} />
              <ContactDetails kind="collection" job={job} emailEnabled={companyFeatures.contactEmailEnabled} />
              <KV label="Deliver to" value={job.dropoffLocation} onMap={() => openMaps(job.dropoffLocation)}
                onCopy={() => copyAddress(job.dropoffLocation, 'Drop-off')} />
              <ContactDetails kind="delivery" job={job} emailEnabled={companyFeatures.contactEmailEnabled} />
              <KV label="Assigned to" value={assignmentDisplayName(job)} placeholder="Unassigned" />
              {(job.latitude != null || job.longitude != null) && (
                <KV label="Coordinates" value={formatCoords(job.latitude, job.longitude)} mono />
              )}
            </div>
          </Panel>

          {canSeeFinancials && (job.state === 'completed' || job.state === 'archived' || job.accountingStatus || job.pricing) && (
            <Panel>
              <PanelTitle title="Accounts" />
              {!financeStatus.ready ? (
                <div className="p-5">
                  {financeStatus.error
                    ? <ReadFailure message={`Prices could not be loaded: ${financeStatus.error}`} retry={financeStatus.retry} />
                    : <p role="status" className="text-[14px] text-ink3">Loading prices…</p>}
                </div>
              ) : (<>
              <div className="px-5 pb-1 pt-2">
                <KV label="Accounting" value={titleCaseStatus(job.accountingStatus || 'unpriced')} />
                <KV label="Price" value={job.pricing ? formatMoneyPence(job.pricing.amountPence, job.pricing.currency) : ''}
                  placeholder="No price recorded" mono />
                {job.pricing?.notes && <KV label="Price notes" value={job.pricing.notes} />}
                {job.invoiceReference && <KV label="Invoice reference" value={job.invoiceReference} mono />}
                {job.invoicedAt && <KV label="Invoiced" value={formatTimestamp(job.invoicedAt)} mono />}
              </div>
              {canUnlockJob && (
                <div className="px-5 pb-5 pt-3">
                  <Button size="sm" variant="secondary" onClick={() => setUnlockInvoiceScope(historyScope)}>Unlock invoice</Button>
                </div>
              )}
              {(canPriceJobs(activeRole ?? undefined) || canInvoiceJobs(activeRole ?? undefined)) &&
                (job.state === 'completed' || job.state === 'archived') && job.accountingStatus !== 'invoiced' && (
                <div className="flex flex-wrap items-center gap-3 px-5 pb-5 pt-3">
                  {canPriceJob && (
                    <Button size="sm" onClick={() => setShowPricing(true)}>
                      {job.pricing ? 'Update price' : 'Add price'}
                    </Button>
                  )}
                  <Link to="/accounts" className="text-[14px] font-semibold text-accent hover:underline">Open Accounts</Link>
                </div>
              )}
              </>)}
            </Panel>
          )}

          </>)}
          {companyFeatures.additionalChargesEnabled && canViewChargeFlags(activeRole ?? undefined) &&
            (chargeFlags.flags.length > 0 || (canPriceJobs(activeRole ?? undefined) && !!job.pricing?.breakdown?.charges.length) || chargeFlags.error) && (
            <Panel><PanelTitle title="Additional charges" />
              <div className="space-y-3 p-5 pt-3 text-[14px]">
                {chargeFlags.error && <ReadFailure message={chargeFlags.error} retry={chargeFlags.retry} />}
                {chargeFlags.flags.map(flag => <div key={flag.id} className="rounded-[10px] bg-surface p-3">
                  <p className="break-words font-semibold">{[flag.code, flag.description].filter(Boolean).join(' · ')}{flag.quantity == null ? '' : ` (${flag.quantity})`}</p>
                  <p className="text-ink2">{titleCaseStatus(flag.status)}{flag.claimedAmountPence == null ? '' : ` · Claimed ${formatMoneyPence(flag.claimedAmountPence, job.pricing?.currency || 'GBP')}`}</p>
                </div>)}
                {canPriceJobs(activeRole ?? undefined) && job.pricing?.breakdown?.charges.map(line => <div key={line.id} className="flex flex-wrap justify-between gap-2">
                  <span className="break-words">{[line.code,line.description].filter(Boolean).join(' · ')}{line.quantity == null ? '' : ` (${line.quantity})`}</span>
                  <span className="font-mono">{formatMoneyPence(line.amountPence,job.pricing?.currency || 'GBP')}</span>
                </div>)}
                {canPriceJob && <Button type="button" size="sm" variant="secondary" onClick={() => setShowPricing(true)}>Price charges</Button>}
              </div>
            </Panel>
          )}
          <Panel>
            <PanelTitle title="Notes" aside={
              canEdit && !archived && !editingCurrentJob ? (
                <button type="button" className="text-[14px] font-semibold text-accent hover:underline"
                  onClick={() => { setNewNote(''); setShowAddNote(true); }}>
                  Add note
                </button>
              ) : undefined
            } />
            <div className="px-5 pb-5 pt-3">
              {editingCurrentJob && form ? (
                <TextArea aria-label="Job notes" form={editFormId} rows={6} value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })} />
              ) : job.notes
                ? <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-ink"><LinkedText text={job.notes} /></p>
                : <p className="text-[14px] text-ink3">No notes yet.</p>}
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-5">
          {activeCompanyId && (
            <Panel>
              <PanelTitle title="Attachments" />
              <div className="p-5 pt-4">
                <AttachmentUploader
                  companyId={activeCompanyId}
                  job={job}
                  archived={archived}
                  canEdit={canEdit}
                  canRemove={activeRole === 'superadmin'}
                />
              </div>
            </Panel>
          )}

          <Panel>
            <PhotoGallery
              key={`${activeCompanyId ?? job.companyId}:${job.id}`}
              companyId={activeCompanyId ?? job.companyId}
              job={job}
              canCorrectEvidence={canCorrectEvidence}
              uploader={canEdit && !archived && activeCompanyId
                ? <PhotoUploader key={`${activeCompanyId}:${job.id}`} companyId={activeCompanyId} job={job} />
                : undefined}
            />
          </Panel>
        </div>
      </div>

      {canSeeHistory && (
        <section className="mt-5 overflow-hidden rounded-[14px] border border-hair bg-elev">
          <h2>
            <button
              type="button"
              onClick={() => setShowHistory((open) => !open)}
              aria-expanded={showHistory}
              aria-controls="job-history"
              className="flex w-full items-center gap-3 px-5 py-4 text-left hover:bg-hover"
            >
              <span className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">Job change history</span>
              <span className="font-mono text-[13px] text-ink3">{visibleLogs.length}</span>
              <ChevronDown size={18} strokeWidth={1.8} aria-hidden
                className={`ml-auto text-ink3 transition-transform duration-[180ms] ease-out ${showHistory ? 'rotate-180' : ''}`} />
            </button>
          </h2>
          {showHistory && (
            <div id="job-history" className="border-t border-rowhair p-5">
              <p className="mb-3 text-[14px] text-ink2">
                {canSeeFinancials
                  ? 'Who created, edited, uploaded evidence, priced, invoiced or archived this job, and when.'
                  : 'Who created, edited, uploaded evidence or archived this job, and when.'}
              </p>
              {logsError && <div className="mb-3"><ReadFailure message={logsError} retry={() => setReadAttempt((n) => n + 1)} /></div>}
              {canSeeFinancials && currentHistory?.financeError && (
                <div className="mb-3"><ReadFailure message={`Price history could not be loaded: ${currentHistory.financeError}`} retry={() => setReadAttempt(n => n + 1)} /></div>
              )}
              {(!currentHistory || currentHistory.loading || currentHistory.financeLoading) && <p role="status" className="mb-3 text-[14px] text-ink3">Loading history…</p>}
              {logsError ? null : visibleLogs.length === 0 && currentHistory && !currentHistory.loading && !currentHistory.financeLoading ? (
                <p className="text-[14px] text-ink3">No audit entries yet.</p>
              ) : (
                <ol className="space-y-2">
                  {visibleLogs.map((l) => {
                    const entry = auditEntrySummary(l);
                    return (
                      <li key={l.id} className="rounded-[10px] bg-surface px-3.5 py-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[14.5px] font-semibold text-ink">{entry.title}</div>
                            {entry.detail && <div className="mt-0.5 whitespace-pre-wrap break-words text-[13.5px] text-ink2"><AuditDetail log={l} detail={entry.detail} /></div>}
                          </div>
                          <span className="eyebrow shrink-0 rounded-full bg-elev px-2.5 py-1">{entry.badge}</span>
                        </div>
                        <div className="mt-1.5 text-[12.5px] text-ink3">
                          {l.userDisplayName}
                          {l.userEmail ? <span> · {l.userEmail}</span> : null}
                          <span> · <span className="font-mono">{formatTimestamp(l.timestamp)}</span></span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`overflow-hidden rounded-[14px] border border-hair bg-elev ${className}`}>{children}</section>;
}

function PanelTitle({ title, owed = 0, aside }: { title: string; owed?: number; aside?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pt-4">
      <h2 className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
      {owed > 0 ? <OutstandingMark>{owed} needed to complete</OutstandingMark> : aside}
    </div>
  );
}

/** A label/value row. An empty required value reads amber, never as an error. */
function KV({
  label, value, placeholder = 'Not set', mono = false, onMap, onCopy,
}: {
  label: string;
  value?: string | null;
  placeholder?: string;
  mono?: boolean;
  onMap?: () => void;
  onCopy?: () => void;
}) {
  const text = (value ?? '').trim();
  // The value wraps and can never be squeezed to nothing: a truncated value with
  // min-width 0 disappears in silence when the label takes the whole row, which is how a
  // phone with larger text ended up showing labels and no vehicle details at all.
  return (
    <div className="flex min-h-12 flex-col gap-0.5 border-t border-rowhair py-2.5 first:border-t-0 sm:flex-row sm:items-center sm:gap-3">
      <span className="text-[14.5px] text-ink2 sm:shrink-0">{label}</span>
      <span className={`min-w-0 flex-1 break-words text-[15px] sm:text-right ${
        text ? (mono ? 'font-mono text-ink' : 'text-ink') : 'text-outstanding'
      }`}>
        {text || placeholder}
      </span>
      {text && (onMap || onCopy) && (
        <span className="-ml-1.5 flex shrink-0 items-center gap-1 sm:ml-0">
          {onMap && (
            <button type="button" onClick={onMap} aria-label={`Open ${label} in Maps`} title="Open in Maps"
              className="flex h-8 w-8 items-center justify-center rounded-[8px] text-accent hover:bg-accenttint">
              <MapPin size={16} />
            </button>
          )}
          {onCopy && (
            <button type="button" onClick={onCopy} aria-label={`Copy ${label}`} title="Copy"
              className="flex h-8 w-8 items-center justify-center rounded-[8px] text-accent hover:bg-accenttint">
              <Copy size={16} />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function openMaps(loc?: string | null) {
  if (!loc) return;
  window.open(`https://maps.apple.com/?q=${encodeURIComponent(loc)}`, '_blank', 'noopener');
}

function auditEntrySummary(log: JobAuditLog): {
  title: string;
  detail?: string;
  badge: string;
} {
  const field = friendlyFieldName(log.fieldChanged);
  const valueField = ['stateChanged', 'statusChanged'].includes(log.actionType) ? 'state' : log.fieldChanged;
  const oldValue = safeAuditValue(valueField, log.oldValue);
  const newValue = safeAuditValue(valueField, log.newValue);
  const imageType = titleCase(log.metadata?.imageType || '');
  const imageCount = log.metadata?.imageCount || '1';
  const fileName = log.metadata?.fileName || primitiveAuditText(log.newValue) || primitiveAuditText(log.oldValue) || '';

  switch (log.actionType) {
    case 'jobCreated':
      if (log.fieldChanged === 'offlineCapture') {
        return {
          title: 'Job created offline and synced later',
          detail:
            oldValue !== '-'
              ? `Device-reported creation time: ${oldValue}. This time is recorded but not independently verified.`
              : log.metadata?.note,
          badge: 'Created',
        };
      }
      return { title: 'Job created', badge: 'Created' };
    case 'jobEdited':
      return summarizeJobEdit(log);
    case 'chargeFlagged':
      return { title: 'Additional charge flagged', detail: newValue !== '-' ? newValue : undefined, badge: 'Edited' };
    case 'chargeFlagDismissed':
      return { title: 'Additional charge dismissed', badge: 'Edited' };
    case 'chargeFlagWithdrawn':
      return { title: 'Additional charge withdrawn', badge: 'Edited' };
    case 'contactsRemoved':
      return { title: 'Contact details removed', detail: log.metadata?.reason || 'Contact details removed after 14 days (retention)', badge: 'Edited' };
    case 'fieldUpdated':
      if (log.fieldChanged === 'collectionContact' || log.fieldChanged === 'deliveryContact') {
        return { title: `${log.fieldChanged === 'collectionContact' ? 'Collection' : 'Delivery'} contact changed`, badge: 'Edited' };
      }
      return {
        title: `${field} changed`,
        detail: `From ${oldValue} to ${newValue}`,
        badge: 'Edited',
      };
    case 'noteAppended':
      return {
        title: 'Note added',
        detail: newValue !== '-' ? newValue : undefined,
        badge: 'Notes',
      };
    case 'noteDeleted':
      return {
        title: 'Note removed',
        detail: oldValue !== '-' ? oldValue : undefined,
        badge: 'Notes',
      };
    case 'statusChanged':
    case 'stateChanged':
      return {
        title: oldValue !== '-' && newValue !== '-'
          ? `Status changed from ${oldValue} to ${newValue}`
          : `Status changed${newValue !== '-' ? ` to ${newValue}` : ''}`,
        badge: 'Status',
      };
    case 'jobArchived':
      return { title: 'Job moved to archive', badge: 'Archive' };
    case 'jobRestored':
      return { title: 'Job restored from archive', badge: 'Archive' };
    case 'imageAdded': {
      const label = imageType || 'Job';
      return {
        title: `${imageCount} ${label} image${imageCount === '1' ? '' : 's'} added`,
        detail: log.metadata?.storagePath ? 'Stored as secure evidence.' : undefined,
        badge: 'Photo',
      };
    }
    case 'imageExcluded':
      return {
        title: `${imageType || 'Evidence'} photo excluded`,
        detail: log.metadata?.reason
          ? `Reason: ${safeAuditValue('reason', log.metadata.reason)}`
          : 'The original file and evidence record were retained.',
        badge: 'Photo',
      };
    case 'imageReclassified': {
      const fromType = titleCase(log.metadata?.fromType || primitiveAuditText(log.oldValue));
      const toType = titleCase(log.metadata?.toType || primitiveAuditText(log.newValue));
      return {
        title: `Evidence type changed${fromType && toType ? ` from ${fromType} to ${toType}` : ''}`,
        detail: log.metadata?.reason
          ? `Reason: ${safeAuditValue('reason', log.metadata.reason)}`
          : undefined,
        badge: 'Photo',
      };
    }
    case 'attachmentAdded':
      return {
        title: 'Attachment added',
        detail: fileName ? safeAuditValue('attachments', fileName) : undefined,
        badge: 'File',
      };
    case 'attachmentRemoved':
      return {
        title: 'Attachment removed',
        detail: fileName ? safeAuditValue('attachments', fileName) : undefined,
        badge: 'File',
      };
    case 'assignmentChanged':
      return {
        title: 'Assignment changed',
        detail: assignmentChangeDetails(log.oldValue, log.newValue),
        badge: 'Assign',
      };
    case 'assignmentCleared':
      return { title: 'Assignment cleared', detail: assignmentChangeDetails(log.oldValue, null), badge: 'Assign' };
    case 'priceAdded':
      return {
        title: 'Price added',
        detail: newValue !== '-' ? newValue : undefined,
        badge: 'Accounts',
      };
    case 'priceUpdated':
      return {
        title: 'Price updated',
        detail: oldValue !== '-' && newValue !== '-' ? `From ${oldValue} to ${newValue}` : newValue,
        badge: 'Accounts',
      };
    case 'jobInvoiced':
      return { title: 'Job marked as invoiced', detail: log.metadata?.invoiceReference
        ? `Invoice reference: ${safeAuditValue('invoiceReference', log.metadata.invoiceReference)}` : undefined, badge: 'Accounts' };
    case 'invoiceUnlocked':
      return { title: 'Invoice unlocked', detail: log.metadata?.reason
        ? `Reason: ${safeAuditValue('reason', log.metadata.reason)}` : undefined, badge: 'Accounts' };
    default:
      return {
        title: field ? `${field} updated` : 'Job updated',
        detail: oldValue !== '-' || newValue !== '-' ? `From ${oldValue} to ${newValue}` : undefined,
        badge: titleCase(log.actionType),
      };
  }
}

function summarizeJobEdit(log: JobAuditLog): {
  title: string;
  detail?: string;
  badge: string;
} {
  const oldFields = auditObject(log.oldValue);
  const newFields = auditObject(log.newValue);
  const fieldKeys = (log.fieldChanged || '')
    .split(',')
    .map((field) => field.trim())
    .filter(Boolean);

  if (fieldKeys.length === 1 && fieldKeys[0] === 'notes') {
    return {
      title: 'Notes edited',
      detail: `From ${safeAuditValue('notes', oldFields?.notes ?? log.oldValue)}\nTo ${safeAuditValue('notes', newFields?.notes ?? log.newValue)}`,
      badge: 'Notes',
    };
  }

  const fields = fieldKeys.length > 0
    ? fieldKeys
    : Array.from(new Set([...Object.keys(oldFields ?? {}), ...Object.keys(newFields ?? {})]));
  const detail = fields
    .slice(0, 4)
    .map((field) => {
      const oldValue = safeAuditValue(field, oldFields?.[field]);
      const newValue = safeAuditValue(field, newFields?.[field]);
      return `${friendlyFieldName(field)}: ${oldValue} to ${newValue}`;
    })
    .join(' · ');
  const overflow = fields.length > 4 ? ` · plus ${fields.length - 4} more` : '';

  return {
    title:
      fields.length === 1
        ? `${friendlyFieldName(fields[0])} changed`
        : `${fields.length} job fields changed`,
    detail: detail ? `${detail}${overflow}` : undefined,
    badge: 'Edited',
  };
}

/** Scan only note values, including the notes inside older multi-field edits. */
function AuditDetail({ log, detail }: { log: JobAuditLog; detail: string }) {
  if (log.fieldChanged === 'notes' || ['noteAppended', 'noteDeleted'].includes(log.actionType)) {
    return <LinkedText text={detail} />;
  }
  if (log.actionType !== 'jobEdited') return <>{detail}</>;
  const oldFields = auditObject(log.oldValue);
  const newFields = auditObject(log.newValue);
  const recordedKeys = (log.fieldChanged ?? '').split(',').map(key => key.trim()).filter(Boolean);
  const fields = recordedKeys.length ? recordedKeys : Array.from(new Set([
    ...Object.keys(oldFields ?? {}), ...Object.keys(newFields ?? {}),
  ]));
  if (!fields.includes('notes')) return <>{detail}</>;
  return <>{fields.slice(0, 4).map((field, index) => {
    const oldText = safeAuditValue(field, oldFields?.[field]);
    const newText = safeAuditValue(field, newFields?.[field]);
    return <Fragment key={field}>
      {index > 0 && ' · '}{friendlyFieldName(field)}: {field === 'notes'
        ? <><LinkedText text={oldText} /> to <LinkedText text={newText} /></>
        : `${oldText} to ${newText}`}
    </Fragment>;
  })}{fields.length > 4 && ` · plus ${fields.length - 4} more`}</>;
}

function auditObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function friendlyFieldName(field: string | null | undefined) {
  return readableHistoryField(field);
}

function safeAuditValue(fieldChanged: string | null | undefined, value: unknown) {
  if (fieldChanged?.startsWith('scheduled')) return readableHistoryValue(fieldChanged, value);
  return value == null || value === '' ? '-' : readableHistoryValue(fieldChanged, value);
}

function primitiveAuditText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toLocaleString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function titleCase(value: string) {
  if (!value) return '';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function friendlyCallableError(error: unknown) {
  const raw = error instanceof Error ? error.message : String(error);
  const message = raw.replace(/^FirebaseError:\s*/i, '');
  if (
    message.toLowerCase().includes('app check') ||
    message.toLowerCase().includes('appcheck') ||
    message.toLowerCase().includes('unauthenticated')
  ) {
    return 'Firebase App Check blocked this request. Hard refresh once, then try again. If it continues, the deployed web App Check key/domain needs checking.';
  }
  if (message.toLowerCase().includes('accounts queue is disabled')) {
    return 'The Accounts Queue is disabled in Company Settings.';
  }
  if (message.toLowerCase().includes('only completed jobs')) {
    return 'Only completed jobs can be priced or invoiced.';
  }
  return message || 'The action failed.';
}

function canEditJob({
  archived,
  job,
  role,
  uid,
}: {
  archived: boolean;
  job: Job;
  role: MemberRole | null;
  uid?: string;
}) {
  if (!uid || !role) return false;
  // Same policy as iOS JobPermissionManager: completed/archived jobs are
  // admin-only; live jobs follow dispatch + assignment.
  if (archived || job.state === 'completed' || job.state === 'archived') {
    return role === 'admin' || role === 'superadmin';
  }
  if (canDispatchJobs(role)) return true;
  if (role === 'driver' && job.assignedUserId === uid) return true;
  return false;
}

function assignmentDisplayName(job: Job): string {
  if (job.assignedSubcontractorName) return job.assignedSubcontractorName;
  if (job.assignedUserDisplayName) return job.assignedUserDisplayName;
  return 'Unassigned';
}

type AssignmentMode = 'clear' | 'internal' | 'subcontractor';

function AssignmentModal({
  companyId,
  job,
  onClose,
}: {
  companyId: string;
  job: Job;
  onClose: () => void;
}) {
  const initialMode: AssignmentMode =
    job.assignmentType === 'internal'
      ? 'internal'
      : job.assignmentType === 'subcontractor'
        ? 'subcontractor'
        : 'clear';
  const [mode, setMode] = useState<AssignmentMode>(initialMode);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [subcontractors, setSubcontractors] = useState<SubcontractorRecord[]>([]);
  const [selectedUserId, setSelectedUserId] = useState(job.assignedUserId ?? '');
  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState(
    job.assignedSubcontractorId ?? ''
  );
  const [loading, setLoading] = useState(true);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [subcontractorError, setSubcontractorError] = useState<string | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMemberError(null);
    setSubcontractorError(null);

    const loadMembers = withReadTimeout(getDocs(
      query(collection(db, 'companies', companyId, 'members'), orderBy('displayName'))
    ))
      .then((snap) =>
        snap.docs
          .map((memberDoc) => {
            const data = memberDoc.data() as Omit<CompanyMember, 'id'>;
            return {
              id: memberDoc.id,
              ...data,
              userId: data.userId || memberDoc.id,
              companyId,
            } as CompanyMember;
          })
          .filter(
            (member) =>
              member.isActive !== false &&
              !['subcontractor', 'accounts', 'viewer'].includes(member.role)
          )
      )
      .catch((loadError) => {
        console.warn('Failed to load assignment members', loadError);
        if (!cancelled) setMemberError(loadError.message);
        return [] as CompanyMember[];
      });

    const loadSubcontractors = withReadTimeout(getDocs(
      query(
        collection(db, 'companies', companyId, 'subcontractors'),
        orderBy('name')
      )
    ))
      .then((snap) =>
        snap.docs
          .map((subcontractorDoc) => ({
            id: subcontractorDoc.id,
            ...(subcontractorDoc.data() as Omit<SubcontractorRecord, 'id'>),
          }))
          .filter((subcontractor) => subcontractor.isActive !== false)
      )
      .catch((loadError) => {
        console.warn('Failed to load subcontractors', loadError);
        if (!cancelled) setSubcontractorError(loadError.message);
        return [] as SubcontractorRecord[];
      });

    void Promise.all([loadMembers, loadSubcontractors]).then(
      ([nextMembers, nextSubcontractors]) => {
        if (cancelled) return;
        setMembers(nextMembers);
        setSubcontractors(nextSubcontractors);
        if (initialMode === 'internal' && !job.assignedUserId && nextMembers[0]) {
          setSelectedUserId(nextMembers[0].userId);
        }
        if (
          initialMode === 'subcontractor' &&
          !job.assignedSubcontractorId &&
          nextSubcontractors[0]
        ) {
          setSelectedSubcontractorId(nextSubcontractors[0].id);
        }
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [companyId, initialMode, job.assignedSubcontractorId, job.assignedUserId]);

  function chooseMode(nextMode: AssignmentMode) {
    setMode(nextMode);
    setError(null);
    if (nextMode === 'internal' && !selectedUserId && members[0]) {
      setSelectedUserId(members[0].userId);
    }
    if (
      nextMode === 'subcontractor' &&
      !selectedSubcontractorId &&
      subcontractors[0]
    ) {
      setSelectedSubcontractorId(subcontractors[0].id);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload: {
        companyId: string;
        jobId: string;
        assignmentType: AssignmentMode;
        assignedUserId?: string;
        assignedSubcontractorId?: string;
      } = {
        companyId,
        jobId: job.id,
        assignmentType: mode,
      };

      if (mode === 'internal') {
        if (!selectedUserId) throw new Error('Choose an internal user.');
        payload.assignedUserId = selectedUserId;
      }
      if (mode === 'subcontractor') {
        if (!selectedSubcontractorId) throw new Error('Choose a subcontractor.');
        payload.assignedSubcontractorId = selectedSubcontractorId;
      }

      const call = httpsCallable<typeof payload, { success: boolean }>(
        functions,
        'assignRecoveryJob'
      );
      await call(payload);
      onClose();
    } catch (saveError) {
      console.error(saveError);
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setSaving(false);
    }
  }

  const canSave =
    mode === 'clear' ||
    (mode === 'internal' && !!selectedUserId) ||
    (mode === 'subcontractor' && !!selectedSubcontractorId);

  return (
    <Modal open onClose={onClose} title="Assign job"
      description="Assignment decides who sees this job on their own board.">
      <form onSubmit={save} className="space-y-4">
        <Field label="Assign to">
          <SegmentedControl
            label="Assignment type"
            value={mode}
            onChange={chooseMode}
            options={[
              { value: 'clear', label: 'Unassigned' },
              { value: 'internal', label: 'Team member' },
              { value: 'subcontractor', label: 'Subcontractor' },
            ]}
          />
        </Field>

        {mode === 'internal' && (
          <Field label="Team member"
            hint={memberError ? <span className="text-st-cancelled">Team list unavailable: {memberError}</span> : undefined}>
            <FieldSelect value={selectedUserId} disabled={loading} onChange={(event) => setSelectedUserId(event.target.value)}>
              <option value="">{loading ? 'Loading…' : members.length === 0 ? 'No active team members' : 'Choose'}</option>
              {members.map((member) => (
                <option key={member.id} value={member.userId}>
                  {member.displayName || member.email || member.userId}
                </option>
              ))}
            </FieldSelect>
          </Field>
        )}

        {mode === 'subcontractor' && (
          <Field label="Subcontractor"
            hint={subcontractorError ? <span className="text-st-cancelled">Subcontractor list unavailable: {subcontractorError}</span> : undefined}>
            <FieldSelect value={selectedSubcontractorId} disabled={loading}
              onChange={(event) => setSelectedSubcontractorId(event.target.value)}>
              <option value="">{loading ? 'Loading…' : subcontractors.length === 0 ? 'No active subcontractors' : 'Choose'}</option>
              {subcontractors.map((subcontractor) => (
                <option key={subcontractor.id} value={subcontractor.id}>
                  {subcontractor.shortName || subcontractor.name}
                </option>
              ))}
            </FieldSelect>
          </Field>
        )}

        {error && <Alert tone="error">{error}</Alert>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={saving} disabled={loading || !canSave}>Save assignment</Button>
        </div>
      </form>
    </Modal>
  );
}

function formFromJob(job: Job): JobFormState {
  return {
    ...contactForm(job),
    registrationNumber: job.registrationNumber ?? '',
    make: job.make ?? '',
    model: job.model ?? '',
    colour: job.colour ?? '',
    fuelType: job.fuelType ?? '',
    transmission: job.transmission ?? '',
    vehicleMileage: job.vehicleMileage ?? '',
    customerName: job.customerName ?? '',
    pickupLocation: job.pickupLocation ?? '',
    dropoffLocation: job.dropoffLocation ?? '',
    courtesyCarRequired: job.courtesyCarRequired ?? false,
    courtesyCarRegistration: job.courtesyCarRegistration ?? '',
    courtesyCarMileage: job.courtesyCarMileage ?? '',
    notes: job.notes ?? '',
  };
}

function collectChangedFields(job: Job, form: JobFormState, emailEnabled: boolean) {
  const changes: Record<string, unknown> = contactPayload(form, emailEnabled, job);
  for (const field of EDITABLE_FIELDS) {
    const oldValue = stringifyValue(job[field.key]) ?? '';
    const newValue = form[field.key].trim();
    if (newValue !== oldValue) changes[field.key] = newValue;
  }
  for (const field of [
    'courtesyCarRegistration',
    'courtesyCarMileage',
  ] as const) {
    const oldValue = stringifyValue(job[field]) ?? '';
    const newValue = form[field].trim();
    if (newValue !== oldValue) changes[field] = newValue;
  }
  if (form.courtesyCarRequired !== (job.courtesyCarRequired ?? false)) {
    changes.courtesyCarRequired = form.courtesyCarRequired;
  }
  if (form.notes.trim() !== (job.notes ?? '').trim()) changes.notes = form.notes.trim();
  return changes;
}

function stringifyValue(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  return String(value);
}
